import { App, reactive } from 'vue';
import { uuid } from '@wittignl/js-core'

import ToastContainer from '../component/toast/ToastContainer.vue'

declare module '@vue/runtime-core' {
    interface ComponentCustomProperties {
        $toast: ToastPlugin
    }
}

export enum ToastType {
    POSITIVE = 'positive',
    NEGATIVE = 'negative',
    WARNING = 'warning',
    NOTICE = 'notice'
}

export interface ToastTypeOptions {
    icon: string
    classes: string[]
}

export interface ToastInstance {
    id: string
    type: ToastTypeOptions
    title: string
    subtitle: string
}

export class ToastPlugin {

    protected types: Map<ToastType, ToastTypeOptions> = new Map();
    protected i18n = null;

    data = reactive({
        toasts: []
    } as { toasts: ToastInstance[] })

    get toasts(): ToastInstance[] {

        return this.data.toasts;
    }

    set toasts(toasts: ToastInstance[]) {

        this.data.toasts = toasts;
    }

    show(type: ToastType, title: string, subtitle?: string): ToastInstance {

        const typeOptions = this.types.get(type);
        if(!typeOptions){
            throw `Toast-type '${type}' not registered`;
        }

        const toast: ToastInstance = {
            id: uuid(),
            type: typeOptions,
            title, subtitle
        };

        this.toasts.push(toast);

        setTimeout(() => this.close(toast), 4000);

        return toast;
    }

    close(toast: ToastInstance) {

        const index = this.toasts.indexOf(toast);
        if(index < 0){
            return;
        }

        this.toasts.splice(index, 1);
    }

    type(name: ToastType, icon: string, classes: string[]): this {

        this.types.set(name, {
            icon, classes
        });

        return this;
    }


    positive(title: string, subtitle?: string) {

        return this.show(ToastType.POSITIVE, title, subtitle);
    }

    negative(title: string, subtitle?: string) {

        return this.show(ToastType.NEGATIVE, title, subtitle);
    }

    warning(title: string, subtitle?: string) {

        return this.show(ToastType.WARNING, title, subtitle);
    }

    notice(title: string, subtitle?: string) {

        return this.show(ToastType.NOTICE, title, subtitle);
    }

    success(title: string, subtitle?: string) {

        return this.positive(title, subtitle);
    }

    error(title: string, subtitle?: string) {

        return this.negative(title, subtitle);
    }

    validation() {

        const i18n = this.i18n;
        if(!i18n){
            return;
        }

        return this.warning(i18n.t('ui.toast.validation'));
    }

    install(app: App, options: any) {

        app.config.globalProperties.$toast = this;
        this.i18n = options?.i18n;

        app.component('ui-toast-container', ToastContainer);
    }
}

export default ToastPlugin;